import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SignatureCanvas } from '@/components/legal/SignatureCanvas';
import { useRecordAuthorization } from '@/hooks/invoicing';

interface AuthorizationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  type: 'pre_repair' | 'post_repair';
  authorizationText: string;
  authorizedAmount: number;
  approvedScopePreview: Array<{ description: string; extended_total: number; type: string }>;
  defaultCustomerName?: string | null;
  /**
   * Slice 5D-6: optional. When supplied, the parent ticket id is
   * threaded through `useRecordAuthorization`'s args so the advisor
   * action emit (`customer_approval_received`) can fire on success.
   * Omitting it is non-fatal — the emit is silently skipped.
   */
  ticketId?: string | null;
}

export const AuthorizationModal = ({
  open, onOpenChange, invoiceId, type, authorizationText, authorizedAmount,
  approvedScopePreview, defaultCustomerName, ticketId,
}: AuthorizationModalProps) => {
  const [customerName, setCustomerName] = useState(defaultCustomerName ?? '');
  const [signature, setSignature] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const recordAuth = useRecordAuthorization();

  const canSubmit = customerName.trim().length > 0 && !!signature && agreed;

  const handleSubmit = async () => {
    if (!canSubmit || !signature) return;
    await recordAuth.mutateAsync({
      invoiceId, type, customerName: customerName.trim(),
      signaturePng: signature, authorizationText,
      ticketId: ticketId ?? undefined,
    });
    onOpenChange(false);
    setCustomerName(defaultCustomerName ?? '');
    setSignature(null);
    setAgreed(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {type === 'pre_repair' ? 'Pre-Repair Authorization' : 'Post-Repair Acknowledgment'}
          </DialogTitle>
          <DialogDescription>
            Capture customer signature for ${authorizedAmount.toFixed(2)}.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-2">
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-3 space-y-1 text-sm">
              <div className="font-medium">Approved scope</div>
              {approvedScopePreview.map((l, i) => (
                <div key={i} className="flex justify-between">
                  <span className="truncate pr-2">{l.description}</span>
                  <span className="tabular-nums">${l.extended_total.toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-1 mt-1 flex justify-between font-semibold">
                <span>Authorized amount</span>
                <span className="tabular-nums">${authorizedAmount.toFixed(2)}</span>
              </div>
            </div>

            <div className="rounded-md border p-3 text-sm whitespace-pre-wrap bg-card">
              {authorizationText}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cust-name">Customer name (typed)</Label>
              <Input id="cust-name" value={customerName}
                onChange={(e) => setCustomerName(e.target.value)} placeholder="Full legal name" />
            </div>

            <div className="space-y-2">
              <Label>Signature</Label>
              <SignatureCanvas onSignatureChange={setSignature} height={150} />
            </div>

            <div className="flex items-start gap-2">
              <Checkbox id="agree" checked={agreed} onCheckedChange={(c) => setAgreed(c === true)} />
              <Label htmlFor="agree" className="text-sm font-normal leading-snug">
                I am the customer or authorized representative and I agree to the terms above.
              </Label>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || recordAuth.isPending}>
            {recordAuth.isPending ? 'Saving…' : 'Sign & save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
